import React from "react";
import { motion } from "framer-motion";
import styles from "../styles/About.module.scss";

const About: React.FC = () => {
  const aboutCards = [
    {
      title: "About INCOIS",
      content:
        "The Indian National Centre for Ocean Information Services (INCOIS) is an autonomous organization under the Ministry of Earth Sciences, Government of India. Established in 1999, INCOIS provides ocean information and advisory services to society, industry, government agencies, and the scientific community.",
      icon: (
        <svg
          width="48"
          height="48"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
          <polyline points="9,22 9,12 15,12 15,22" />
        </svg>
      ),
    },
    {
      title: "Why Citizen Reporting Matters",
      content:
        "Coastal communities are the first to witness ocean hazards. Your real-time reports help authorities respond faster, validate scientific data, and create comprehensive hazard maps. Every report contributes to building safer coastal environments for millions of people.",
      icon: (
        <svg
          width="48"
          height="48"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="M23 21v-2a4 4 0 00-3-3.87" />
          <path d="M16 3.13a4 4 0 010 7.75" />
        </svg>
      ),
    },
    {
      title: "How This Platform Works",
      content:
        "Our AI-powered system combines citizen reports with satellite data, weather models, and social media analysis. Machine learning algorithms validate reports, predict hazard patterns, and generate real-time alerts for coastal communities and emergency responders.",
      icon: (
        <svg
          width="48"
          height="48"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
        </svg>
      ),
    },
  ];

  return (
    <motion.div
      className={styles.aboutPage}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4 }}
    >
      {/* Wave Background Pattern */}
      <div className={styles.waveBackground}>
        <svg viewBox="0 0 1200 120" preserveAspectRatio="none">
          <motion.path
            d="M0,0V46.29c47.79,22.2,103.59,32.17,158,28,70.36-5.37,136.33-33.31,206.8-37.5C438.64,32.43,512.34,53.67,583,72.05c69.27,18,138.3,24.88,209.4,13.08,36.15-6,69.85-17.84,104.45-29.34C989.49,25,1113-14.29,1200,52.47V0Z"
            fill="rgba(0, 153, 255, 0.1)"
            animate={{
              opacity: [0.1, 0.2, 0.1],
              y: [0, -5, 0],
            }}
            transition={{
              duration: 12,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />
        </svg>
      </div>

      <div className={styles.container}>
        {/* Header Section with Logo */}
        <motion.div
          className={styles.header}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <div className={styles.logoSection}>
            <div className={styles.logo}>
              <svg width="80" height="80" viewBox="0 0 100 100" fill="none">
                <circle
                  cx="50"
                  cy="50"
                  r="45"
                  fill="url(#oceanGradient)"
                  stroke="rgba(0, 153, 255, 0.3)"
                  strokeWidth="2"
                />
                <path
                  d="M25 45 Q35 35, 45 45 T65 45 T85 45"
                  stroke="white"
                  strokeWidth="3"
                  fill="none"
                  opacity="0.8"
                />
                <path
                  d="M20 55 Q30 45, 40 55 T60 55 T80 55"
                  stroke="white"
                  strokeWidth="2"
                  fill="none"
                  opacity="0.6"
                />
                <path
                  d="M30 65 Q40 55, 50 65 T70 65"
                  stroke="white"
                  strokeWidth="2"
                  fill="none"
                  opacity="0.4"
                />
                <circle cx="50" cy="35" r="8" fill="rgba(255, 255, 255, 0.9)" />
                <path
                  d="M46 31 L50 35 L54 31"
                  stroke="#0099ff"
                  strokeWidth="2"
                  fill="none"
                />
                <defs>
                  <linearGradient
                    id="oceanGradient"
                    x1="0%"
                    y1="0%"
                    x2="100%"
                    y2="100%"
                  >
                    <stop offset="0%" stopColor="#0099ff" />
                    <stop offset="50%" stopColor="#0066cc" />
                    <stop offset="100%" stopColor="#003d7a" />
                  </linearGradient>
                </defs>
              </svg>
            </div>
            <h1 className={styles.title}>
              <span className={styles.titleHighlight}>OceanGuard</span>
            </h1>
          </div>
          <p className={styles.subtitle}>
            Bridging the gap between coastal communities and ocean science
            through collaborative monitoring and real-time hazard reporting
          </p>
        </motion.div>

        {/* About Cards Grid */}
        <div className={styles.cardsGrid}>
          {aboutCards.map((card, index) => (
            <motion.div
              key={index}
              className={styles.aboutCard}
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 + index * 0.2 }}
              whileHover={{
                y: -10,
                scale: 1.02,
                transition: { duration: 0.3 },
              }}
            >
              <motion.div
                className={styles.cardIcon}
                whileHover={{ scale: 1.1, rotate: 5 }}
                transition={{ duration: 0.3 }}
              >
                {card.icon}
              </motion.div>

              <h3 className={styles.cardTitle}>{card.title}</h3>
              <p className={styles.cardContent}>{card.content}</p>

              <div className={styles.cardGlow}></div>
            </motion.div>
          ))}
        </div>

        {/* Mission Statement */}
        <motion.div
          className={styles.missionSection}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 1.0 }}
        >
          <div className={styles.missionCard}>
            <h2 className={styles.missionTitle}>Our Mission</h2>
            <p className={styles.missionText}>
              To create a comprehensive, community-driven ocean hazard
              monitoring system that empowers coastal communities with real-time
              information, enhances emergency response capabilities, and
              contributes to global ocean safety research through collaborative
              data collection and advanced analytics.
            </p>

            <div className={styles.stats}>
              <div className={styles.stat}>
                <span className={styles.statNumber}>50K+</span>
                <span className={styles.statLabel}>Coastal Communities</span>
              </div>
              <div className={styles.stat}>
                <span className={styles.statNumber}>24/7</span>
                <span className={styles.statLabel}>Monitoring</span>
              </div>
              <div className={styles.stat}>
                <span className={styles.statNumber}>15+</span>
                <span className={styles.statLabel}>Languages</span>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Technology Section */}
        <motion.div
          className={styles.techSection}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 1.2 }}
        >
          <h2 className={styles.techTitle}>Powered by Advanced Technology</h2>
          <div className={styles.techGrid}>
            <div className={styles.techItem}>
              <span className={styles.techIcon}>🛰️</span>
              <span className={styles.techName}>Satellite Data</span>
            </div>
            <div className={styles.techItem}>
              <span className={styles.techIcon}>🤖</span>
              <span className={styles.techName}>AI/ML Analytics</span>
            </div>
            <div className={styles.techItem}>
              <span className={styles.techIcon}>📱</span>
              <span className={styles.techName}>Mobile Integration</span>
            </div>
            <div className={styles.techItem}>
              <span className={styles.techIcon}>🌐</span>
              <span className={styles.techName}>Real-time Processing</span>
            </div>
          </div>
        </motion.div>

        {/* Footer Section */}
        <motion.div
          className={styles.footerSection}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 1.4 }}
        >
          <div className={styles.footerContent}>
            <div className={styles.versionInfo}>
              <h3>Version Information</h3>
              <p>
                <strong>Version:</strong> 2.1.0
              </p>
              <p>
                <strong>Last Updated:</strong> September 2025
              </p>
              <p>
                <strong>Build:</strong> Production
              </p>
            </div>

            <div className={styles.credits}>
              <h3>Credits & Acknowledgments</h3>
              <p>
                <strong>Developed by:</strong> INCOIS Ocean Safety Team
              </p>
              <p>
                <strong>Technology Partners:</strong> React, Firebase, Leaflet
              </p>
              <p>
                <strong>Data Sources:</strong> NOAA, ESA Sentinel, Local Weather
                Stations
              </p>
              <p>
                <strong>Special Thanks:</strong> Coastal communities worldwide
                for their valuable contributions
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default About;
